import type { QueryClient } from '@tanstack/react-query';
import { queryKeys } from './queryKeys';
import type { MinimalLeadData } from '../../types';

/**
 * Single write path for seeding a campaign's FULL leads array into the query
 * cache. ALWAYS writes both key variants:
 *
 *   ['leads','campaign',cid]            — legacy/no-email key (invalidation targets)
 *   ['leads','campaign',cid,userEmail]  — the key useLeadsForCampaign reads
 *
 * Before this helper existed there were three hand-rolled writers, one of
 * which (useActionStatusPolling) wrote only the email variant — grids reading
 * the bare key stayed stale. Route every full-array write through here.
 *
 * Note: per-lead STATUS PATCHERS (syncLeadStatus* closures in the route
 * files) are a different operation — they patch one lead inside existing
 * arrays and already dual-write; leave them inline.
 *
 * Seeded data counts as fresh (dataUpdatedAt = now), so consumers with their
 * own staleTime (useLeadsForCampaign: 2 min) hit cache on mount instead of
 * refetching — that's the whole point of prefetch-seeding.
 */
export const seedCampaignLeads = (
  queryClient: QueryClient,
  campaignId: string,
  userEmail: string | null | undefined,
  leads: MinimalLeadData[],
): void => {
  if (!campaignId) return;
  queryClient.setQueryData(queryKeys.leads.byCampaign(campaignId), leads);
  if (userEmail) {
    queryClient.setQueryData(queryKeys.leads.byCampaign(campaignId, userEmail), leads);
  }
};
